import { useFormContext, type FieldPath } from "react-hook-form"
import { FixedSizeList } from "react-window"

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

export interface SelectOption {
  id?: number | string
  value?: string
  label: string
  description?: string
}

interface FormSelectProps<T extends Record<string, unknown>> {
  name: FieldPath<T>
  label?: string
  options: SelectOption[]
  placeholder: string
  parseValueAsInt?: boolean
  className?: string
  onChange?: (value: string | number) => void
  disabled?: boolean
  hiddenLabel?: boolean
}

export function FormSelect<T extends Record<string, unknown>>({
  name,
  label,
  options,
  placeholder,
  disabled = false,
  parseValueAsInt = false,
  className,
  hiddenLabel = false,
  onChange,
}: FormSelectProps<T>) {
  const itemSize = 40
  const maxHeight = 400
  const listHeight = Math.min(options?.length * itemSize, maxHeight)

  const form = useFormContext<T>()

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const selectedLabel = options.find((item) => {
          return item.value === field.value || item.id === field.value
        })?.label

        const triggerTitle = selectedLabel || placeholder

        return (
          <FormItem className={cn("w-full", className)}>
            {label && (
              <FormLabel style={{ display: hiddenLabel ? "none" : "block" }}>
                {label}
              </FormLabel>
            )}
            <Select
              onValueChange={(value: string) => {
                if (value) {
                  const parsed = parseValueAsInt ? parseInt(value) : value
                  field.onChange(parsed)
                  onChange?.(parsed)
                }
              }}
              value={
                field.value !== undefined &&
                field.value !== null &&
                field.value !== ""
                  ? parseValueAsInt
                    ? field.value.toString()
                    : String(field.value)
                  : ""
              }
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger
                  title={triggerTitle}
                  className={cn(
                    "w-full min-w-0",
                    field.value ? "bg-white" : "bg-gray-50",
                  )}
                >
                  {field.value ? (
                    <SelectValue className="min-w-0 flex-1 truncate">
                      {selectedLabel}
                    </SelectValue>
                  ) : (
                    <span className="text-muted inline-block min-w-0 flex-1 truncate">
                      {placeholder}
                    </span>
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <FixedSizeList
                  width={"100%"}
                  height={listHeight}
                  itemCount={options.length}
                  itemSize={40}
                >
                  {({
                    index,
                    style,
                  }: {
                    index: number
                    style: React.CSSProperties
                  }) => (
                    <SelectItem
                      key={
                        options[index].id ||
                        options[index].value ||
                        `item-${options[index].label}`
                      }
                      value={
                        options[index].value ||
                        options[index].id?.toString() ||
                        `item-${options[index].label}`
                      }
                      style={{
                        ...style,
                      }}
                    >
                      {options[index].description || options[index].label}
                    </SelectItem>
                  )}
                </FixedSizeList>
              </SelectContent>
            </Select>
            <FormMessage className="form-error" />
          </FormItem>
        )
      }}
    />
  )
}
